import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ProductAddonForm from "../../components/crm/ProductAddonForm";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  getAdminProductAddon,
  updateAdminProductAddon,
} from "../../services/order.service";
import { ProductAddon, ProductAddonUpdateRequest } from "../../types/order";

export default function CrmAddonsEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [addon, setAddon] = useState<ProductAddon | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Add-on List", to: "/crm/addons" },
      { label: "Edit Add On", to: `/crm/addons/${id}/edit` },
    ]);
  }, [id, setBreadcrumbs]);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        navigate("/crm/addons");
        return;
      }

      setIsLoading(true);
      try {
        const response = await getAdminProductAddon(id);
        setAddon(response);
      } catch (error) {
        console.error("Error loading add-on:", error);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const handleSubmit = async (data: ProductAddonUpdateRequest) => {
    if (!id) return;

    try {
      await updateAdminProductAddon(id, data);
      showSnackbar("Add-on updated successfully", "success");
      navigate("/crm/addons");
    } catch (error) {
      console.error("Error updating add-on:", error);
      showSnackbar("Failed to update add-on", "error");
    }
  };

  if (isLoading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ minHeight: 320 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Box>
        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
              Add-on Info
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <ProductAddonForm
              addon={addon}
              onSubmit={handleSubmit}
              onCancel={() => navigate("/crm/addons")}
            />
          </Box>
        </Paper>
      </Box>
    </Stack>
  );
}
